import { ReactNode, useEffect } from 'react'
import { PaginationBar } from '@systemeio/components'
import { useRouter } from 'next/router'
import { limitObj_10_25_50 } from 'shared/route-query/constants/pagination-limit-array'
import type { LimitObjType } from 'shared/types/swr-pagination-interface'
import { OrderEnum } from 'shared/types/swr-pagination-interface'
import type { TableProps } from './table'

export interface DataWithPaginationInterface<T> {
  hasMore: boolean
  items: T[]
}

export interface PaginationInterface {
  startFromId?: number | string
  order: OrderEnum
  hasMore?: boolean
  isFirst?: boolean
  staticUrl?: boolean
}

export interface TableLimitPaginationProps<T> extends Pick<
  TableProps<T>,
  'data' | 'limiter' | 'paginator'
> {
  limitObj?: LimitObjType
}

const TableLimitPagination = <T extends { id: number | string }>({
  data,
  limiter,
  paginator,
  limitObj = limitObj_10_25_50,
}: TableLimitPaginationProps<T>): ReactNode => {
  const router = useRouter()
  const { pagination, setPagination } = paginator!
  const { limit, setLimit } = limiter!
  const { limit: limitAmount } = router.query

  const isPaginationShown =
    (data &&
      limiter &&
      paginator &&
      ((paginator.pagination.order !== 'prev' && paginator.hasMore) ||
        paginator.pagination.order === 'prev' ||
        !paginator.pagination.isFirst)) ||
    (data && limiter && limiter.limit && limiter.limit !== limitObj.short)

  useEffect(() => {
    if (paginator) {
      paginator.setPagination(prev => ({
        ...prev,
        hasMore: paginator.hasMore,
        isFirst: (prev.order === 'prev' && !paginator.hasMore) || prev.startFromId === undefined,
      }))
    }
  }, [data])

  useEffect(() => {
    if (limitAmount) {
      setLimit(Number(limitAmount))
    } else {
      setLimit(limitObj.short)
    }
  }, [limitAmount, setLimit, limitObj.short])

  useEffect(() => {
    if (pagination.startFromId && data && !pagination.staticUrl) {
      router.push(
        {
          query: {
            limit: limit,
            startFromId: pagination.startFromId,
            order: pagination.order,
          },
        },
        undefined,
        {
          shallow: true,
        },
      )
    } else if (data && !pagination.startFromId && !pagination.staticUrl) {
      router.push(
        {
          query: {
            limit: limit,
          },
        },
        undefined,
        {
          shallow: true,
        },
      )
    }
  }, [pagination, limit, data])

  if (!isPaginationShown) {
    return null
  }

  const hasItems = data !== undefined && data.items.length > 0

  return (
    <PaginationBar
      limit={limit}
      hasNextPage={hasItems && (!!pagination.hasMore || pagination.order === OrderEnum.prev)}
      hasPreviousPage={
        hasItems &&
        !pagination.isFirst &&
        !(pagination.order === OrderEnum.prev && !pagination.hasMore)
      }
      onNextPage={() =>
        setPagination(prev => ({
          ...prev,
          isFirst: false,
          startFromId: data?.items.at(-1)?.id ?? undefined,
          order: OrderEnum.next,
        }))
      }
      onPreviousPage={() =>
        setPagination(prev => ({
          ...prev,
          startFromId: data?.items[0].id || undefined,
          order: OrderEnum.prev,
        }))
      }
      onLimitChange={nextLimit => {
        setLimit(nextLimit)
        setPagination(prev => ({
          ...prev,
          startFromId: undefined,
          order: OrderEnum.next,
        }))
      }}
      selectProps={{ options: Object.values(limitObj) }}
    />
  )
}

export default TableLimitPagination
